import React from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { useImpression } from '@thd-olt-component-react/impression';
import { ImageHotspot } from '@thd-olt-component-react/image-hotspot';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { VideoHeroImage } from './VideoHeroImage';
import { selectPreferredImage, getHeroImageType } from '../utils/publicLinks';

const {
  shape,
  string,
  number,
  arrayOf,
  bool
} = proptypes;

const HeroFlattenImageComponent = ({
  id = '',
  altText: altTextProp = '',
  layoutRendererPrerenderedChildIndex = undefined,
  link = '',
  mobilePreviewImage = undefined,
  previewImage = undefined,
  proAnalyticsCampaign = undefined,
  proAnalyticsComponent = undefined,
  title = undefined,
  isB2C = false,
  proCustomerSegment = undefined,
  shouldLoopVideo = true,
  sourceData = null,
}) => {
  const altText = altTextProp || title;
  const isB2B = !isB2C;
  const mobileMinWidthBreakpoint = isB2B ? '(min-width: 640px)' : '(min-width: 768px)';
  const largeViewportImageSrcUrl = selectPreferredImage({
    damImage: previewImage,
  });
  const smallViewportImageSrcUrl = selectPreferredImage({
    damImage: mobilePreviewImage,
  });

  const heroImageType = getHeroImageType(largeViewportImageSrcUrl);

  const impressionContextData = useImpression({
    data: {
      id,
      name: 'HeroFlattenImage',
      component: 'Hero',
      position: layoutRendererPrerenderedChildIndex + 1,
      type: 'content',
      ...(heroImageType ? { category: heroImageType } : {}),
    },
  });

  const isHeroImageHotspot = !!previewImage?.imageHotspot?.id;
  const hasImageHotspot = !!(mobilePreviewImage?.imageHotspot?.id || previewImage?.imageHotspot?.id);
  const isMobileViewport = useBreakpoint('md').lesser;
  const roundedClasses = classNames({
    'sui-overflow-hidden sui-rounded-[12px]': isB2C,
  });
  const linkRoundedClasses = classNames('sui-block', roundedClasses);

  function handleClickAnalytics() {
    if (isB2B && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('hero-flatten.content-click', {
        widgetId: id,
        widgetTemplates: proCustomerSegment,
        widgetCampaign: proAnalyticsCampaign,
        widgetComponent: proAnalyticsComponent,
      });
    }
  }

  function handlePictureClick(event) {
    if (hasImageHotspot) {
      event.preventDefault();
    }
    handleClickAnalytics();
  }

  const imageClasses = 'sui-w-full sui-h-auto sui-top-0 sui-left-0';

  const heroPictureLinkClasses = classNames(linkRoundedClasses, {
    'sui-cursor-default': hasImageHotspot,
  });

  const heroContent = heroImageType === 'mp4'
    ? (
      <a
        aria-label={`Navigate to ${altText || 'No description available'}`}
        href={link}
        target="_self"
        // eslint-disable-next-line react/no-unknown-property
        clickid={impressionContextData.clickID}
        ref={impressionContextData.ref}
      >
        <VideoHeroImage
          desktopVideoUrl={largeViewportImageSrcUrl}
          mobileVideoUrl={smallViewportImageSrcUrl}
          altText={altText}
          shouldLoopVideo={shouldLoopVideo}
          isBentoHero
          fullWidth
        />
      </a>
    ) : (
      <a
        href={link}
        key={id}
        onClick={handlePictureClick}
        ref={impressionContextData.ref}
        // eslint-disable-next-line react/no-unknown-property
        clickid={impressionContextData.clickID}
        className={heroPictureLinkClasses}
      >
        <picture>
          <source
            className={imageClasses}
            alt={`Image for ${altText}`}
            srcSet={largeViewportImageSrcUrl}
            media={mobileMinWidthBreakpoint}
            data-testid="hero-flatten-image-source"
          />
          {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
          <img
            className={imageClasses}
            alt={altText}
            src={smallViewportImageSrcUrl}
            data-testid="hero-flatten-image-img"
          />
        </picture>
      </a>
    );

  if (isHeroImageHotspot) {
    const mobileHotspot = mobilePreviewImage?.imageHotspot || previewImage?.imageHotspot;
    const desktopHotspot = previewImage?.imageHotspot || mobileHotspot;
    const hotspotSourceData = isMobileViewport ? sourceData?.mobile : sourceData?.desktop;

    return (
      <ErrorBoundary name="image-hotspot">
        <QueryProvider cacheKey="image-hotspot">
          <ImageHotspot
            componentId={isMobileViewport
              ? mobileHotspot?.id : desktopHotspot?.id}
            componentClass={isMobileViewport
              ? mobileHotspot?.componentName : desktopHotspot?.componentName}
            position={layoutRendererPrerenderedChildIndex + 1}
            parentId={id}
            lazyLoadImages={isB2C}
            shouldLoopVideo={shouldLoopVideo}
            sourceData={hotspotSourceData}
          >
            {heroContent}
          </ImageHotspot>
        </QueryProvider>
      </ErrorBoundary>
    );
  }

  if (!largeViewportImageSrcUrl && !smallViewportImageSrcUrl) {
    return null;
  }

  return heroContent;
};

HeroFlattenImageComponent.propTypes = {
  link: string,
  id: string,
  altText: string,
  mobilePreviewImage: shape({
    damContentSelector: shape({
      assetData: arrayOf(
        shape({
          selectedImageUrl: string,
        })
      ),
    }),
    damDownloadedContent: shape({
      url: string,
    }),
    imageHotspot: shape({
      id: string,
      componentName: string,
    })
  }),
  previewImage: shape({
    damContentSelector: shape({
      assetData: arrayOf(
        shape({
          selectedImageUrl: string,
        })
      ),
    }),
    damDownloadedContent: shape({
      url: string,
    }),
    imageHotspot: shape({
      id: string,
      componentName: string,
    })
  }),
  title: string,
  layoutRendererPrerenderedChildIndex: number,
  proAnalyticsCampaign: string,
  proAnalyticsComponent: string,
  isB2C: bool,
  proCustomerSegment: string,
  shouldLoopVideo: bool,
  sourceData: shape({})
};

export { HeroFlattenImageComponent };